import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import { MessageContent, Thread } from '../../interface';
import { Attachment, SkyproMessagesModule } from '@skyeng/libs/skypro/messages';
import { map } from 'rxjs';
import { ViewportSizes, ViewportSizeService } from '@skyeng/libs/skypro/misc';
import { convertHtmlListToParagraphs, getThreadDeliveryStatus } from '../../helpers';
import isSameDay from 'date-fns/isSameDay';
import { DatePipe } from '@angular/common';
import { QuillModule } from 'ngx-quill';
import { MatMenuModule } from '@angular/material/menu';
import { SkyI18nModule } from '@skyeng/libs/shared/i18n';
import { VimCoreUserModule } from '@skyeng/libs/vimbox-core/user';
import { DsIconModule } from '@skyeng/libs/shared/ui-kit-v2/icon';
import { FeatureFlagsService } from '@skyeng/libs/skypro/shared';
import { DsTooltipModule } from '@skyeng/libs/shared/ui-kit-v2/tooltip';

@Component({
  selector: 'skypro-communication-flat-view-message',
  templateUrl: 'communication-flat-view-message.html',
  styleUrl: 'communication-flat-view-message.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkyI18nModule,
    VimCoreUserModule,
    DsIconModule,
    MatMenuModule,
    QuillModule,
    SkyproMessagesModule,
    DatePipe,
    DsTooltipModule,
  ],
})
export class CommunicationFlatViewMessageComponent {
  private featureFlagService = inject(FeatureFlagsService);
  private viewportSizeService = inject(ViewportSizeService);

  message = input.required<MessageContent>();
  canEdit = input(false);
  canDelete = input(false);
  baseApiHost = input('');

  // Описан тут: packages/skypro/student-care/src/app/feature-flags.ts
  showDebugDraft = this.featureFlagService.isFeatureFlagEnabled('debugDrafts');

  @Output() edit = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() createAiMessageReport = new EventEmitter<void>();

  isMobile$ = this.viewportSizeService.viewport$.pipe(map(() => this.viewportSizeService.isBefore(ViewportSizes.m)));
  deliveryStatus = computed(() => {
    const message = this.message();
    return getThreadDeliveryStatus(message.createdAt, message.author.type, message.meta ?? {});
  });

  deliveryError = computed(() => {
    const message = this.message();

    return message?.meta?.['deliveryError'] ?? 'Неизвестная ошибка';
  });

  isAiGenerated = computed(() => {
    const message = this.message();
    return message?.meta?.['isAiGenerated'] ?? false;
  });

  amoTaskResolution = computed(() => {
    const message = this.message();
    return message?.meta?.['amoTaskResolution'] ?? null;
  });

  @ViewChild('messageBodyRef', { read: ElementRef }) messageBodyRef: ElementRef<HTMLElement>;

  getDebugLink(threadId: number): string {
    return `${this.baseApiHost()}/student-care/api/gateway/v1/curator-talks/create-draft-answer/${threadId}`;
  }

  editMessage(): void {
    this.edit.emit();
  }

  doCreateAiMessageReport(): void {
    this.createAiMessageReport.emit();
  }

  deleteMessage(): void {
    const message = 'Вы уверены что хотите удалить это сообщение? Удаление нельзя будет отменить.';
    const isConfirmed = confirm(message);
    if (!isConfirmed) {
      return;
    }

    this.delete.emit();
  }

  trackByAttachment(_index: number, attachment: Attachment): string {
    return attachment.url;
  }

  getFlatMessageBody(messageBody: string): string {
    return convertHtmlListToParagraphs(messageBody);
  }

  getMessageContentType(content: MessageContent): string {
    const isThread = (content as Thread).status !== undefined;
    return isThread ? 'thread' : 'message';
  }

  isToday(date: string): boolean {
    return isSameDay(new Date(date), Date.now());
  }
}
